import React, { ReactNode, useState, useEffect, useMemo } from "react";
import {
  MessageCircle,
  User as UserIcon,
  LogOut,
  Settings as SettingsIcon,
  ChevronLeft,
  Search,
  Send,
  Package,
  BarChart,
  AlertTriangle,
  History,
  Building2,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import useAuthStore, { isAnyAdmin, type UserRole } from "@/stores/authStore";
import { useActivityStore } from "@/stores/activityStore";
import useChatStore from "@/stores/chatStore";
import { BusinessSwitcher } from "./BusinessSwitcher";
import { useEffectiveRole } from "@/components/auth/RouteGuards";
import { BranchSwitcher } from "./BranchSwitcher";
import NotificationBell from "@/components/notifications/NotificationBell";
import NotificationStack from "@/components/notifications/NotificationStack";
import HelpMenu from "@/components/help/HelpMenu";
import ApprovalPrompt from "@/components/approvals/ApprovalPrompt";
import CurrentShiftBadge from "@/components/shifts/CurrentShiftBadge";
import { CurrencySwitcher } from "../group/CurrencySwitcher";

// ─── Role display labels — kept in sync with Sidebar.tsx ────────────
const ROLE_LABELS: Record<UserRole, string> = {
  tenant_owner: "Tenant Owner",
  super_admin: "Super Admin",
  business_admin: "Business Administrator",
  staff: "Staff",
  cashier: "Cashier",
};

interface HeaderProps {
  title: string;
  children?: ReactNode;
}

function getInitials(name: string | undefined): string {
  if (!name) return "U";
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0]!)
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export const Header: React.FC<HeaderProps> = ({ title, children }) => {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const effRole = useEffectiveRole() || user?.role;
  const isAdmin = effRole ? isAnyAdmin(effRole) : false;

  const {
    activities,
    loading: activitiesLoading,
    fetchActivities,
  } = useActivityStore();
  const { unreadCounts, users, messages, setActiveChat } = useChatStore();

  const totalUnreadMessages = useMemo(
    () => Object.values(unreadCounts).reduce((sum, count) => sum + count, 0),
    [unreadCounts],
  );

  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedConversation, setSelectedConversation] = useState<
    string | null
  >(null);
  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [lastSeenActivityCount, setLastSeenActivityCount] = useState(0);

  // Real-time clock — updates every second.
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formattedDate = format(currentTime, "MMM d, yyyy");
  const formattedTime = format(currentTime, "h:mm a");

  // Fetch once on mount + poll every 60s. fetchActivities intentionally
  // omitted from deps — including it causes a refetch loop because the
  // store updates `activities` on every fetch, re-rendering this component.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    void fetchActivities();
    const interval = setInterval(() => void fetchActivities(), 60000);
    return () => clearInterval(interval);
  }, []);

  const newActivityCount = Math.max(
    0,
    activities.length - lastSeenActivityCount,
  );

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to log out. Please try again.");
    }
  };

  const handleActivitySheetOpen = (open: boolean) => {
    if (open) {
      setLastSeenActivityCount(activities.length);
    }
  };

  const handleMessageClick = (userId: string) => {
    setSelectedConversation(userId);
    setActiveChat(userId);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (messageInput.trim() && selectedConversation) {
      setMessageInput("");
    }
  };

  const filteredUsers = users.filter((u) =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const location = useLocation();
  const isGroupView = location.pathname.startsWith("/group");

  return (
    <header className="sticky top-0 z-40 flex h-16 w-full items-center justify-between gap-2 border-b border-border bg-background px-3 sm:px-6">
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
        {children}
        <h1 className="text-lg font-semibold text-foreground truncate">
          {title}
        </h1>
        {isGroupView ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#4CAF50]/10 text-[#4CAF50] text-sm font-medium">
              <Building2 className="h-3.5 w-3.5" />
              <span>Group view · all businesses</span>
            </div>
            <button
              onClick={() => navigate("/portfolio")}
              className="w-full text-left text-xs text-muted-foreground hover:text-foreground px-3"
            >
              ← Exit to single business
            </button>
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-1">
            <BusinessSwitcher />
            <BranchSwitcher />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
        <div className="hidden md:flex items-center gap-2">
          <div className="flex items-center rounded-md bg-muted px-2.5 py-1">
            <span className="text-xs font-medium text-muted-foreground">
              {formattedDate}
            </span>
          </div>
          <div className="flex items-center rounded-md bg-muted px-2.5 py-1">
            <span className="text-xs font-medium text-muted-foreground tabular-nums">
              {formattedTime}
            </span>
          </div>
        </div>

        <CurrentShiftBadge />
        {/* Phones: currency and help move out of the way so the business name fits. */}
        <div className="hidden sm:flex">
          <CurrencySwitcher />
        </div>
        <NotificationBell />
        <div className="hidden sm:flex">
          <HelpMenu />
        </div>
        <ApprovalPrompt />
        <NotificationStack />

        {/* ── Activity History Sheet ── */}
        <Sheet onOpenChange={handleActivitySheetOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative h-9 w-9 rounded-md hover:bg-muted"
              aria-label="Activity history"
            >
              <History className="h-4 w-4 text-muted-foreground" />
              {newActivityCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -right-1 -top-1 h-4 min-w-4 rounded-full px-1 text-[10px] tabular-nums"
                >
                  {newActivityCount > 9 ? "9+" : newActivityCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-[400px] sm:w-[480px] p-0">
            <SheetHeader className="px-6 py-4 border-b border-border">
              <SheetTitle className="text-base font-semibold">
                Activity History
              </SheetTitle>
            </SheetHeader>
            <ScrollArea className="h-[calc(100vh-4rem)]">
              {activitiesLoading ? (
                <div className="flex flex-col gap-3 p-6">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="animate-pulse rounded-md bg-muted h-16 w-full"
                    />
                  ))}
                </div>
              ) : activities.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                  <div className="rounded-full bg-muted p-3">
                    <History className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      No activity yet
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Actions across your business will appear here.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {activities.map((activity) => (
                    <div
                      key={activity._id}
                      className="px-6 py-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-foreground">
                              {activity.action}
                            </p>
                            <Badge
                              variant="outline"
                              className="text-[10px] py-0 h-4 bg-accent text-accent-foreground border-accent"
                            >
                              {activity.category}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {activity.description}
                          </p>
                          <p className="text-[11px] text-muted-foreground/70">
                            by{" "}
                            <span className="font-medium">
                              {typeof activity.user === "object" &&
                              activity.user
                                ? activity.user.name
                                : typeof activity.user === "string"
                                  ? activity.user
                                  : "System"}
                            </span>
                          </p>
                        </div>
                        <span className="text-[11px] text-muted-foreground/70 whitespace-nowrap pt-0.5">
                          {format(
                            new Date(activity.createdAt),
                            "MMM d, h:mm a",
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </SheetContent>
        </Sheet>

        {/* ── Messages Sheet ── */}
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative h-9 w-9 rounded-md hover:bg-muted"
              aria-label="Messages"
            >
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
              {totalUnreadMessages > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -right-1 -top-1 h-4 min-w-4 rounded-full px-1 text-[10px] tabular-nums"
                >
                  {totalUnreadMessages > 9 ? "9+" : totalUnreadMessages}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-[400px] sm:w-[480px] p-0">
            <div className="flex h-full flex-col">
              <SheetHeader className="px-6 py-4 border-b border-border">
                <SheetTitle className="text-base font-semibold">
                  {isAdmin ? "Team Messages" : "Messages"}
                </SheetTitle>
              </SheetHeader>

              {selectedConversation ? (
                <div className="flex flex-col h-[calc(100vh-4rem)]">
                  <div className="flex items-center gap-3 px-6 py-3 border-b border-border">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setSelectedConversation(null)}
                      aria-label="Back to conversations"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-accent text-accent-foreground text-xs">
                        {getInitials(
                          users.find((u) => u.userId === selectedConversation)
                            ?.name,
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {
                          users.find((u) => u.userId === selectedConversation)
                            ?.name
                        }
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {users.find((u) => u.userId === selectedConversation)
                          ?.status === "online"
                          ? "Online"
                          : "Offline"}
                      </p>
                    </div>
                  </div>

                  <ScrollArea className="flex-1 px-6 py-4">
                    <div className="space-y-3">
                      {(messages[selectedConversation] || []).map((message) => {
                        const isMe = message.senderId === user?.id;
                        return (
                          <div
                            key={message.id}
                            className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`max-w-[75%] rounded-lg px-3 py-2 ${
                                isMe
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-foreground"
                              }`}
                            >
                              <p className="text-sm leading-relaxed">
                                {message.content}
                              </p>
                              <p
                                className={`text-[10px] mt-1 ${
                                  isMe
                                    ? "text-primary-foreground/70"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {format(new Date(message.timestamp), "h:mm a")}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>

                  <form
                    onSubmit={handleSendMessage}
                    className="p-4 border-t border-border"
                  >
                    <div className="flex gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        type="submit"
                        size="icon"
                        disabled={!messageInput.trim()}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="flex flex-col h-[calc(100vh-4rem)]">
                  <div className="p-4 border-b border-border">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search conversations..."
                        className="pl-9 h-9"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  <ScrollArea className="flex-1">
                    {filteredUsers.length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                        <div className="rounded-full bg-muted p-3">
                          <MessageCircle className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {searchQuery
                              ? "No matches"
                              : "No conversations yet"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {searchQuery
                              ? "Try a different search term."
                              : "Conversations will appear here."}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="p-2 space-y-0.5">
                        {filteredUsers.map((chatUser) => {
                          const lastMessage =
                            messages[chatUser.userId]?.[
                              messages[chatUser.userId].length - 1
                            ];
                          const unread = unreadCounts[chatUser.userId] || 0;

                          return (
                            <button
                              key={chatUser.userId}
                              type="button"
                              className="w-full flex items-center gap-3 rounded-md p-3 hover:bg-muted transition-colors text-left"
                              onClick={() =>
                                handleMessageClick(chatUser.userId)
                              }
                            >
                              <div className="relative shrink-0">
                                <Avatar className="h-9 w-9">
                                  <AvatarFallback className="bg-accent text-accent-foreground text-xs">
                                    {getInitials(chatUser.name)}
                                  </AvatarFallback>
                                </Avatar>
                                {chatUser.status === "online" && (
                                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-sm font-medium text-foreground truncate">
                                    {chatUser.name}
                                  </p>
                                  {lastMessage && (
                                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                      {format(
                                        new Date(lastMessage.timestamp),
                                        "h:mm a",
                                      )}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                  {lastMessage?.content || "No messages yet"}
                                </p>
                              </div>
                              {unread > 0 && (
                                <Badge
                                  variant="destructive"
                                  className="h-5 min-w-5 rounded-full px-1.5 text-[10px] tabular-nums shrink-0"
                                >
                                  {unread > 9 ? "9+" : unread}
                                </Badge>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="relative flex items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label="Account menu"
            >
              <Avatar className="h-9 w-9 ring-2 ring-border hover:ring-primary/40 transition-colors">
                <AvatarImage src={user?.profileImage} />
                <AvatarFallback className="bg-accent text-accent-foreground text-xs font-medium">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {user && (
              <>
                <DropdownMenuLabel className="font-normal py-2.5">
                  <div className="flex flex-col space-y-0.5">
                    <p className="text-sm font-medium leading-none truncate">
                      {user.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {ROLE_LABELS[user.role]}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => navigate("/settings?tab=profile")}>
              <UserIcon className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            {isAdmin && (
              <>
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <SettingsIcon className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/report")}>
                  <BarChart className="mr-2 h-4 w-4" />
                  <span>Reports</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/low-stock")}>
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  <span>Low Stock</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/orders")}>
                  <Package className="mr-2 h-4 w-4" />
                  <span>Orders</span>
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-destructive focus:text-destructive focus:bg-destructive/10"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
